import React, { useState, useEffect, useRef } from "react";
import Swal from "sweetalert2";
import { POST, PUT, GET, newFetch } from "../../../../helpers/fetch";
import { config } from "../../../../config/index";
import Modal from "react-modal";
import "./styles.css";

const FaceVerification = ({
  user,
  type,
  imgSrc,
  src,
  videoRef,
  faceapi,
  setStartClockInOut,
  featuresOnOff,
  t
}) => {
  const canvasRef = useRef();
  const videoHeight = 290;
  const videoWidth = 400;
  const [initializing, setInitializing] = useState(true);
  const [faceVerificationInterval, setFaceVerificationInterval] = useState();
  const [counter, setCounter] = useState(0);

  const padding2 = 120; // adjust this to your needs
  let height2 = 295 + padding2;
  let heightPx2 = height2 + "px";
  let heightOffset2 = height2 / 2;
  let offsetPx2 = heightOffset2 + "px";

  const style2 = {
    border: "0",
    borderRadius: "20px",
    bottom: "auto",
    height: heightPx2, // set height
    left: "50%",
    padding: "2rem",
    position: "fixed",
    right: "auto",
    top: "40%", // start from center
    transform: "translate(-50%,-" + offsetPx2 + ")", // adjust top "up" based on height
    width: "90%",
    overflow: "hidden",
    maxWidth: "420px",
  };

  const stopFaceVerification = async (type) => {
    setInitializing(false);
    clearInterval(faceVerificationInterval);

    if (videoRef.current?.srcObject == null) {
      await src.getVideoTracks().forEach((track) => {
        track.stop();
        src.removeTrack(track);
      });
    } else {
      await videoRef.current.srcObject.getVideoTracks().forEach((track) => {
        track.stop();
        videoRef.current.srcObject.removeTrack(track);
      });
    }

    if (type === "stop") {
      window.location.reload(true);
    }
  };

  const captureSnapshot = async () => {
    let ctx = canvasRef.current.getContext("2d");
    let img = new Image();
    ctx.drawImage(videoRef.current, 0, 0, videoWidth, videoHeight);
    img.src = canvasRef.current.toDataURL("image/jpeg");
    img.width = 420;
    let snapshot = canvasRef.current.toDataURL("image/jpeg");
    let api_path = `/associate/${user.id}/save_verified_img`;

    await newFetch(POST, api_path, { snapshot, type });
  };

  const handleVideoOnPlay = () => {
    let verifyFace = setInterval(async () => {
      if (!videoRef.current) return;

      const video = videoRef.current;

      if (video.videoWidth === 0 || video.videoHeight === 0) {
        console.warn("Video dimensions are not available yet.");
        return;
      }

      const videoWidth = video.videoWidth - 240;
      const videoHeight = video.videoHeight - 190;

      if (initializing) {
        setInitializing(false);
      }

      // Ensure canvasRef.current is valid
      if (!canvasRef.current) {
        console.warn("Canvas reference is null.");
        return;
      }

      canvasRef.current.innerHTML = "";
      const canvas = faceapi.createCanvasFromMedia(video);
      canvasRef.current.appendChild(canvas);

      const displaySize = {
        width: videoWidth,
        height: videoHeight,
      };

      const descriptions = [];
      const img = await faceapi.fetchImage(`${config.SERVER_URL}/${imgSrc}`);

      const detections2 = await faceapi
        .detectSingleFace(
          img,
          new faceapi.TinyFaceDetectorOptions({
            inputSize: 160,
            scoreThreshold: 0.5,
          })
        )
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (detections2) {
        descriptions.push(detections2?.descriptor);

        const labeledDescriptors = [
          new faceapi.LabeledFaceDescriptors("Verified", descriptions),
        ];
        const faceMatcher = new faceapi.FaceMatcher(labeledDescriptors, 0.41);

        if (canvasRef.current) {
          faceapi.matchDimensions(canvasRef.current, displaySize);

          const detections = await faceapi
            .detectSingleFace(
              video,
              new faceapi.TinyFaceDetectorOptions({
                inputSize: 160,
                scoreThreshold: 0.85,
              })
            )
            .withFaceLandmarks()
            .withFaceDescriptor();

          if (detections) {
            const resizedDetections = faceapi.resizeResults(
              detections,
              displaySize
            );
            const bestMatch = faceMatcher.findBestMatch(detections.descriptor);
            const ctx = canvas.getContext("2d");

            ctx.clearRect(0, 0, videoWidth, videoHeight);

            const box = {
              x: resizedDetections.detection.box.x,
              y: resizedDetections.detection.box.y,
              width: resizedDetections.detection.box.width,
              height: resizedDetections.detection.box.height,
            };

            const drawOptions = { lineWidth: 2 };
            const drawBox = new faceapi.draw.DrawBox(box, drawOptions);
            drawBox.draw(canvas);

            let landmarkPositions = resizedDetections.landmarks.positions;

            for (let i = 0; i < landmarkPositions.length; i++) {
              let x_val = landmarkPositions[i]._x;
              let y_val = landmarkPositions[i]._y;
              ctx.beginPath();
              ctx.arc(x_val, y_val, 1, 0, Math.PI * 2, false);
              ctx.fillStyle = "#CECCCB";
              ctx.fill();
              ctx.closePath();
            }

            if (bestMatch.label === "Verified") {
              captureSnapshot();
              clearInterval(verifyFace);
              setCounter(0);
              stopFaceVerification("match");

              Swal.fire({
                title: `${t('Face Recognized')}`,
                text: `${t('Proceeding now to clock in')}`,
                icon: "success",
              });

              setTimeout(() => {
                setStartClockInOut(true);
              }, 1000);
            }
          }
        }
      }

      setCounter((prevCount) => prevCount + 1);
    }, 1000);

    setFaceVerificationInterval(verifyFace);
  };

  useEffect(() => {
    if (counter > 15) {
      setCounter(0);
      Swal.fire({
        title: `${t('Sorry, failed to recognize your face id')}`,
        text: `${t('You may be not registered to this client or please try again')}`,
        icon: "error",
      });
    }
  }, [counter]);

  useEffect(() => {
    return () => clearInterval(faceVerificationInterval);
  }, []);

  return (
    <React.Fragment>
      {featuresOnOff.feature === "faceRecognition" && (
        <div className="capturephoto__container">
          <div className="d-flex justify-content-center profile webcamera">
            <video
              id="stream"
              ref={videoRef}
              playsInline
              width="400"
              height="290"
              onPlay={handleVideoOnPlay}
              autoPlay
            ></video>
            <canvas
              id="capture"
              width="400"
              height="290"
              ref={canvasRef}
              className="position-absolute"
              style={{ left: 15 }}
            ></canvas>
          </div>
          <div
            className="capturephoto__container-confirm"
            style={{
              marginTop: 30,
            }}
          >
            {initializing ? (
              <p>{t('Initializing Face Verification')}</p>
            ) : (
              <>
                <p>{t('Initializing')}</p>
                <p>{t('Please move your face closer to the screen')}</p>
              </>
            )}
            <button
              type="button"
              className="btn btn-design btn-brand btn-clockinout"
              style={{
                backgroundColor: "#5156BE",
              }}
              onClick={() => {
                stopFaceVerification("stop");
              }}
            >
              {t('Cancel')}
            </button>
          </div>
        </div>
      )}
    </React.Fragment>
  );
};

export default FaceVerification;
